import axiosInstance from "@app/axiosInstance";
import { useCompany } from "@app/providers/CompanyContextProvider";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";
import { T_ReportComment } from "./types";

export const useReportCommentQueries = (reportId: number | null) => {
  const { selectedCompany } = useCompany();
  const companyId = selectedCompany?.companyId;
  const queryClient = useQueryClient();
  const queryKey = ["reportComments", companyId, reportId];

  const invalidateReportData = () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey.includes("reports"),
    });
  };

  const getComments = () => {
    const { data: comments, isPending: isCommentsPending } = useQuery({
      queryKey,
      queryFn: async () => {
        try {
          const response = await axiosInstance.get<T_ReportComment[]>(
            `/admin/${companyId}/report/${reportId}/comment`,
          );
          return response.data;
        } catch (error) {
          if (axios.isAxiosError(error) && error.response?.status === 404) {
            return [] as T_ReportComment[];
          }
          throw error;
        }
      },
      enabled: !!companyId && !!reportId,
    });
    return { comments, isCommentsPending };
  };

  const postComment = useMutation({
    mutationFn: (comment: string) =>
      axiosInstance
        .post<T_ReportComment>(
          `/admin/${companyId}/report/${reportId}/comment`,
          { comment },
        )
        .then((response) => response.data),
    onSuccess: invalidateReportData,
  });

  const editComment = useMutation({
    mutationFn: (input: { reportCommentId: number; comment: string }) =>
      axiosInstance
        .put<T_ReportComment>(
          `/admin/${companyId}/report/${reportId}/comment/${input.reportCommentId}`,
          { comment: input.comment },
        )
        .then((response) => response.data),
    onSuccess: invalidateReportData,
  });

  const deleteComment = useMutation({
    mutationFn: (reportCommentId: number) =>
      axiosInstance.delete(
        `/admin/${companyId}/report/${reportId}/comment/${reportCommentId}`,
      ),
    onSuccess: invalidateReportData,
  });

  return { getComments, postComment, editComment, deleteComment };
};
